import { create } from "zustand";

import type {
  CNSProjectAnalytics,
  CNSProjectNextStepsView,
  CNSProjectNextStepsScope,
} from "@/models/cns";
import { fetchProjectAnalytics, fetchProjectNextSteps } from "@/services/projectService";
import { getErrorMessage } from "@/types/problem-detail";

/** Per-project analytics state slice. */
export interface ProjectAnalyticsSlice {
  data: CNSProjectAnalytics | null;
  loading: boolean;
  error: string | null;
  /** Timestamp (ms) of the last successful fetch; null when never loaded. */
  loadedAt: number | null;
}

/** Per-project next-steps state slice. */
export interface ProjectNextStepsSlice {
  scope: CNSProjectNextStepsScope;
  data: CNSProjectNextStepsView | null;
  loading: boolean;
  error: string | null;
  /** Timestamp (ms) of the last successful fetch; null when never loaded. */
  loadedAt: number | null;
}

interface ProjectDashboardEntry {
  analytics: ProjectAnalyticsSlice;
  nextSteps: ProjectNextStepsSlice;
  /** Shared in-flight fetch promise per (projectId, kind, scope) to dedupe concurrent callers. */
  _inFlight: {
    analytics?: Promise<void>;
    nextSteps?: Partial<Record<CNSProjectNextStepsScope, Promise<void>>>;
  };
}

const emptyAnalytics: ProjectAnalyticsSlice = {
  data: null,
  loading: false,
  error: null,
  loadedAt: null,
};

const emptyNextSteps = (scope: CNSProjectNextStepsScope): ProjectNextStepsSlice => ({
  scope,
  data: null,
  loading: false,
  error: null,
  loadedAt: null,
});

function freshEntry(): ProjectDashboardEntry {
  return {
    analytics: { ...emptyAnalytics },
    nextSteps: emptyNextSteps("all"),
    _inFlight: {},
  };
}

interface ProjectDashboardStore {
  /** Map keyed by project public id. Entries are created lazily on first read/load. */
  byProject: Record<string, ProjectDashboardEntry>;

  /** Idempotent: ensures an entry exists for the project and returns it. */
  ensureEntry: (projectId: string) => ProjectDashboardEntry;

  /** Fetch analytics for the project. No-op if a fresh successful fetch is cached. */
  loadAnalytics: (projectId: string, options?: { force?: boolean }) => Promise<void>;

  /**
   * Fetch next steps for the project under the given scope. Subsequent calls with a different
   * scope replace the slice; scope changes always refetch.
   */
  loadNextSteps: (
    projectId: string,
    scope?: CNSProjectNextStepsScope,
    options?: { force?: boolean },
  ) => Promise<void>;

  /** Convenience: load both slices in parallel for the project root. */
  loadDashboard: (projectId: string, options?: { scope?: CNSProjectNextStepsScope }) => Promise<void>;

  /** Clears cached dashboard slices so UI cannot show stale analytics or next-steps. */
  invalidate: (projectId: string) => void;

  /** Clears only the next-steps slice; preserves analytics cache. */
  invalidateNextSteps: (projectId: string) => void;

  /**
   * Clears cache and refetches dashboard slices. Use after mutations that affect
   * project-home waiting copy or analytics.
   */
  refreshProjectDashboard: (
    projectId: string,
    options?: { scope?: CNSProjectNextStepsScope; reloadAnalytics?: boolean },
  ) => Promise<void>;
}

const isCacheFresh = (loadedAt: number | null, maxAgeMs = 30_000): boolean => {
  if (loadedAt == null) return false;
  return Date.now() - loadedAt < maxAgeMs;
};

export const useProjectDashboardStore = create<ProjectDashboardStore>((set, get) => ({
  byProject: {},

  ensureEntry: (projectId: string) => {
    const existing = get().byProject[projectId];
    if (existing) return existing;
    const entry = freshEntry();
    set((state) => ({
      byProject: { ...state.byProject, [projectId]: entry },
    }));
    return entry;
  },

  loadAnalytics: async (projectId: string, options) => {
    const run = async (): Promise<void> => {
      const entry = get().ensureEntry(projectId);
      if (!options?.force && isCacheFresh(entry.analytics.loadedAt) && !entry.analytics.error) {
        return;
      }
      if (entry._inFlight.analytics) {
        await entry._inFlight.analytics;
        // force:true must refetch after the in-flight call finishes (e.g. post-upload).
        if (!options?.force) return;
        const after = get().ensureEntry(projectId);
        if (after._inFlight.analytics) {
          await after._inFlight.analytics;
          return;
        }
      }

      const promise = (async () => {
        set((state) => {
          const e = state.byProject[projectId] ?? freshEntry();
          return {
            byProject: {
              ...state.byProject,
              [projectId]: {
                ...e,
                analytics: { ...e.analytics, loading: true, error: null },
              },
            },
          };
        });

        try {
          const data = await fetchProjectAnalytics(projectId);
          set((state) => {
            const e = state.byProject[projectId] ?? freshEntry();
            return {
              byProject: {
                ...state.byProject,
                [projectId]: {
                  ...e,
                  analytics: { data, loading: false, error: null, loadedAt: Date.now() },
                },
              },
            };
          });
        } catch (e) {
          const message = getErrorMessage(e, "Failed to load project analytics");
          set((state) => {
            const cur = state.byProject[projectId] ?? freshEntry();
            return {
              byProject: {
                ...state.byProject,
                [projectId]: {
                  ...cur,
                  analytics: { ...cur.analytics, loading: false, error: message },
                },
              },
            };
          });
        } finally {
          set((state) => {
            const cur = state.byProject[projectId];
            if (!cur) return state;
            const { analytics: _omit, ...restInFlight } = cur._inFlight;
            void _omit;
            return {
              byProject: {
                ...state.byProject,
                [projectId]: { ...cur, _inFlight: restInFlight },
              },
            };
          });
        }
      })();

      set((state) => {
        const cur = state.byProject[projectId] ?? freshEntry();
        return {
          byProject: {
            ...state.byProject,
            [projectId]: {
              ...cur,
              _inFlight: { ...cur._inFlight, analytics: promise },
            },
          },
        };
      });

      await promise;
    };

    await run();
  },

  loadNextSteps: async (projectId: string, scope = "all", options) => {
    const entry = get().ensureEntry(projectId);
    const sameScope = entry.nextSteps.scope === scope;
    if (
      !options?.force &&
      sameScope &&
      isCacheFresh(entry.nextSteps.loadedAt) &&
      !entry.nextSteps.error
    ) {
      return;
    }
    const inFlight = entry._inFlight.nextSteps?.[scope];
    if (inFlight) {
      await inFlight;
      if (!options?.force) return;
      const after = get().ensureEntry(projectId);
      const afterInFlight = after._inFlight.nextSteps?.[scope];
      if (afterInFlight) {
        await afterInFlight;
        return;
      }
    }

    const promise = (async () => {
      set((state) => {
        const e = state.byProject[projectId] ?? freshEntry();
        const carry = e.nextSteps.scope === scope ? e.nextSteps : emptyNextSteps(scope);
        return {
          byProject: {
            ...state.byProject,
            [projectId]: {
              ...e,
              nextSteps: { ...carry, scope, loading: true, error: null },
            },
          },
        };
      });

      try {
        const data = await fetchProjectNextSteps(projectId, scope);
        set((state) => {
          const e = state.byProject[projectId] ?? freshEntry();
          if (e.nextSteps.scope !== scope) return state;
          return {
            byProject: {
              ...state.byProject,
              [projectId]: {
                ...e,
                nextSteps: { scope, data, loading: false, error: null, loadedAt: Date.now() },
              },
            },
          };
        });
      } catch (e) {
        const message = getErrorMessage(e, "Failed to load project next steps");
        set((state) => {
          const cur = state.byProject[projectId] ?? freshEntry();
          if (cur.nextSteps.scope !== scope) return state;
          return {
            byProject: {
              ...state.byProject,
              [projectId]: {
                ...cur,
                nextSteps: { ...cur.nextSteps, scope, loading: false, error: message },
              },
            },
          };
        });
      } finally {
        set((state) => {
          const cur = state.byProject[projectId];
          if (!cur) return state;
          const scopeMap = { ...(cur._inFlight.nextSteps ?? {}) } as Partial<
            Record<CNSProjectNextStepsScope, Promise<void>>
          >;
          delete scopeMap[scope];
          return {
            byProject: {
              ...state.byProject,
              [projectId]: {
                ...cur,
                _inFlight: { ...cur._inFlight, nextSteps: scopeMap },
              },
            },
          };
        });
      }
    })();

    set((state) => {
      const cur = state.byProject[projectId] ?? freshEntry();
      return {
        byProject: {
          ...state.byProject,
          [projectId]: {
            ...cur,
            _inFlight: {
              ...cur._inFlight,
              nextSteps: { ...(cur._inFlight.nextSteps ?? {}), [scope]: promise },
            },
          },
        },
      };
    });

    await promise;
  },

  loadDashboard: async (projectId, options) => {
    await Promise.all([
      get().loadAnalytics(projectId),
      get().loadNextSteps(projectId, options?.scope ?? "all"),
    ]);
  },

  invalidate: (projectId: string) => {
    set((state) => {
      const cur = state.byProject[projectId];
      if (!cur) return state;
      const scope = cur.nextSteps.scope;
      return {
        byProject: {
          ...state.byProject,
          [projectId]: {
            analytics: { ...emptyAnalytics },
            nextSteps: emptyNextSteps(scope),
            _inFlight: {},
          },
        },
      };
    });
  },

  invalidateNextSteps: (projectId: string) => {
    set((state) => {
      const cur = state.byProject[projectId];
      if (!cur) return state;
      const scope = cur.nextSteps.scope;
      const { nextSteps: _omit, ...restInFlight } = cur._inFlight;
      void _omit;
      return {
        byProject: {
          ...state.byProject,
          [projectId]: {
            ...cur,
            nextSteps: emptyNextSteps(scope),
            _inFlight: restInFlight,
          },
        },
      };
    });
  },

  refreshProjectDashboard: async (projectId, options) => {
    const scope =
      options?.scope ?? get().byProject[projectId]?.nextSteps.scope ?? "all";
    const reloadAnalytics = options?.reloadAnalytics ?? true;
    if (reloadAnalytics) {
      get().invalidate(projectId);
    } else {
      get().invalidateNextSteps(projectId);
    }
    await Promise.all([
      reloadAnalytics
        ? get().loadAnalytics(projectId, { force: true })
        : Promise.resolve(),
      get().loadNextSteps(projectId, scope, { force: true }),
    ]);
  },
}));
